import { HTMLAttributes, FC, useEffect, useState, useCallback } from "react";
import clsx from "classnames";
import { useStyles } from "./GameMediaPreview.style";
import { Icon } from "../../core/Icon/Icon";
import { Image } from "../../core/Image/Image";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import {
  getYtbVideoThumbnail,
  isImageUrl,
  isYouTubeUrl,
  YtbThumbnailQuality,
} from "../../../models/media";
import { YtbVideoPlayer } from "../YtbVideoPlayer/YtbVideoPlayer";
import { Dialog } from "../../core/Dialog/Dialog";
import ArrowWrapper from "../ArrowWrapper/ArrowWrapper";
import {
  MEDIA_PREVIEW_REVERT_EVENT,
  requestMediaPreviewRevert,
} from "../../../utils/media-preview-revert";
import PlayVideo1x from "../../../pages/GameDetails/assets/play_video_1x.webp";
import PlayVideo2x from "../../../pages/GameDetails/assets/play_video_2x.webp";
import PlayVideo3x from "../../../pages/GameDetails/assets/play_video_3x.webp";
import { useMediaPreview } from "../../../hooks/useMediaPreview";
import { useCookieConsent } from "../../../hooks/useCookieConsent";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isFireTv } from "../../../utils/platform";
import { useTranslation } from "react-i18next";

interface IGameMediaPreviewProps {
  mediaList: string[];
  onClose: (event: React.MouseEvent<HTMLSpanElement>) => void;
  initialMediaIndex?: number;
  enableVideoLoop?: boolean;
  setCurrentMediaIndex?: (index: number) => void;
  thumbnailQuality?: YtbThumbnailQuality;
}

export const GameMediaPreview: FC<
  HTMLAttributes<HTMLDivElement> & IGameMediaPreviewProps
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    mediaList,
    onClose,
    initialMediaIndex = 0,
    enableVideoLoop = true,
    setCurrentMediaIndex,
    thumbnailQuality = "sd",
  } = props;

  const [requestedIndexes, setRequestedIndexes] = useState<number[]>([]);
  const isSingleItem = mediaList.length === 1;
  const firstVideoIndex = mediaList.findIndex(isYouTubeUrl);
  useMediaPreview({
    setCurrentMediaIndex,
  });
  const { setActiveElement } = useGamepadContext();
  const { isFunctionalAllowed } = useCookieConsent();

  const attachInitialRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || isSingleItem) return;
      el.setAttribute("data-gamepad-no-auto-scroll", "true");
      setActiveElement(el);
      el.removeAttribute("data-gamepad-no-auto-scroll");
    },
    [setActiveElement, isSingleItem],
  );

  /**
   * Single item: play immediately
   * Multi item: only one YouTube iframe mounted at a time to avoid scroll jank.
   */
  const [playingIndex, setPlayingIndex] = useState<number | null>(
    isSingleItem ? initialMediaIndex : null,
  );

  // Keyboard Enter is handled globally by GamepadProvider --> .click() --> onClick.
  const playVideo = (index: number) => {
    setPlayingIndex(index);
    setRequestedIndexes((prev) =>
      prev.includes(index) ? prev : [...prev, index],
    );
  };

  /**
   * Single item: keep "false" so Back closes dialog directly (skip revert flow).
   * Multi item: Sync `data-video-playing` on dialog so GamepadProvider can read state.
   */
  useEffect(() => {
    const dialog = document.querySelector(
      "dialog[open]",
    ) as HTMLDialogElement | null;
    if (!dialog) return;
    dialog.dataset.videoPlaying =
      !isSingleItem && playingIndex !== null ? "true" : "false";
    return () => {
      dialog.dataset.videoPlaying = "false";
    };
  }, [playingIndex, isSingleItem]);

  /**
   * Single item: has no thumbnail state to revert to — skip.
   * Multi item: Listen for external revert requests (Back button, pre-scroll intent).
   */
  useEffect(() => {
    if (isSingleItem) return;
    const onRevert = () => setPlayingIndex(null);
    window.addEventListener(MEDIA_PREVIEW_REVERT_EVENT, onRevert);
    return () =>
      window.removeEventListener(MEDIA_PREVIEW_REVERT_EVENT, onRevert);
  }, [isSingleItem]);

  const renderItem = (media: string, index: number) => {
    if (isImageUrl(media)) {
      return <Image src={media} className={classes.image} />;
    }

    const isActive = playingIndex === index;

    if (isYouTubeUrl(media)) {
      const thumbnail = (
        <div className={classes.thumbnailWrapper}>
          <Image
            src={getYtbVideoThumbnail(media, thumbnailQuality)}
            className={classes.image}
          />
          <StaticImage
            src={PlayVideo1x}
            src2x={PlayVideo2x}
            src3x={PlayVideo3x}
            className={classes.playVideoIcon}
          />
        </div>
      );

      const useOverlay = isFireTv && index !== firstVideoIndex;
      if (useOverlay) {
        return isActive ? <div className={classes.image} /> : thumbnail;
      }

      if (!isActive) return thumbnail;
      return (
        <YtbVideoPlayer
          url={media}
          loop={enableVideoLoop}
          className={clsx(classes.image, classes.iframe)}
          title={t("GameDetail_Video number", { number: index + 1 })}
          thumbnailQuality={thumbnailQuality}
          isPlayRequested={requestedIndexes.includes(index)}
        />
      );
    }

    const canPlayFile = isFunctionalAllowed || requestedIndexes.includes(index);
    if (!isActive || !canPlayFile) {
      return (
        <div className={classes.thumbnailWrapper}>
          <video
            src={media}
            className={classes.image}
            preload="metadata"
            muted
            onContextMenu={(e) => e.preventDefault()}
          />
          <StaticImage
            src={PlayVideo1x}
            src2x={PlayVideo2x}
            src3x={PlayVideo3x}
            className={classes.playVideoIcon}
          />
        </div>
      );
    }

    return (
      <video
        src={media}
        className={classes.image}
        controls
        autoPlay
        muted
        loop={enableVideoLoop}
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
      />
    );
  };

  return (
    <Dialog
      className={clsx(classes.root, props.className)}
      hideLayout
      open={true}
    >
      <Icon
        aria-label={t("A11y_Close")}
        name="icon-bold-close"
        className={classes.closeIcon}
        size={24}
        onClick={onClose}
        id="close-dialog"
        data-button-sound="back"
      />
      <ArrowWrapper
        className={clsx(
          classes.mediaList,
          isSingleItem && classes.mediaListSingle,
        )}
        indexToScroll={initialMediaIndex}
        onBeforeArrowScroll={requestMediaPreviewRevert}
        enableScrollSnap
      >
        {mediaList.map((media, index) => {
          const isPlayableVideo = !isImageUrl(media);
          const isInitial = initialMediaIndex === index;
          return (
            <div
              ref={isInitial ? attachInitialRef : undefined}
              data-gamepad={
                isInitial ? "default-selected" : `media-preview-${index}`
              }
              data-scroll-inline="center"
              tabIndex={0}
              key={index}
              onClick={isPlayableVideo ? () => playVideo(index) : undefined}
              role={isPlayableVideo ? "button" : undefined}
              style={isPlayableVideo ? { cursor: "pointer" } : undefined}
              data-selected-class={classes.selected}
              data-index={index}
            >
              {renderItem(media, index)}
            </div>
          );
        })}
      </ArrowWrapper>
      {isFireTv &&
        playingIndex !== null &&
        playingIndex !== firstVideoIndex &&
        isYouTubeUrl(mediaList[playingIndex]) && (
          <div className={classes.videoOverlay}>
            <YtbVideoPlayer
              url={mediaList[playingIndex]}
              loop={enableVideoLoop}
              className={clsx(
                classes.image,
                classes.iframe,
                classes.overlayVideo,
              )}
              title={t("GameDetail_Video number", { number: playingIndex + 1 })}
              thumbnailQuality={thumbnailQuality}
              isPlayRequested={requestedIndexes.includes(playingIndex)}
            />
          </div>
        )}
    </Dialog>
  );
};
